import { useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { Permission } from '@aid/shared';
import { useHasPermission } from '../../auth/useHasPermission';
import { useT } from '../../locales';
import { useCreateRole } from './api/useRoles';
import { RoleForm, ROLE_FORM_ID, type RoleFormValues } from './components/RoleForm';
import { RoleEditorHeader } from './components/RoleEditorHeader';
import { RoleUnsavedChangesModal } from './components/RoleUnsavedChangesModal';
import type { PermissionSummary } from './permission-metadata';
import { useRoleUnsavedChanges } from './useRoleUnsavedChanges';
import './roles.css';

const EMPTY_ROLE_FORM_VALUES: RoleFormValues = {
  name: '',
  description: '',
  isActive: true,
  permissionIds: [],
};

export function RoleCreatePage() {
  const navigate = useNavigate();
  const t = useT();
  const createRole = useCreateRole();
  const canCreate = useHasPermission(Permission.ROLES_CREATE);
  const [draft, setDraft] = useState<RoleFormValues>(EMPTY_ROLE_FORM_VALUES);
  const [permissionSummary, setPermissionSummary] = useState<PermissionSummary>({
    permissionCount: 0,
    moduleCount: 0,
  });
  const unsavedChanges = useRoleUnsavedChanges({
    onDiscard: () => navigate('/roles'),
  });

  if (!canCreate) {
    return <EmptyState title={t('pages.roles.noCreatePermission')} compact />;
  }

  const handleSubmit = async (values: RoleFormValues) => {
    try {
      await createRole.mutateAsync(values);
      unsavedChanges.markClean();
      message.success(t('pages.roles.createSuccess'));
      navigate('/roles');
    } catch {
      message.error(t('pages.roles.createError'));
    }
  };

  return (
    <div className="role-editor-page">
      <RoleEditorHeader
        title={draft.name.trim() || t('pages.roles.createTitle')}
        description={draft.description}
        isActive={draft.isActive}
        selectedPermissionCount={permissionSummary.permissionCount}
        selectedModuleCount={permissionSummary.moduleCount}
        isDirty={unsavedChanges.isDirty}
        saving={createRole.isPending}
        saveLabel={t('pages.roles.create')}
        onBack={unsavedChanges.requestDiscard}
        onDiscard={unsavedChanges.requestDiscard}
        formId={ROLE_FORM_ID}
      />

      <RoleForm
        mode="create"
        onSubmit={handleSubmit}
        onCancel={unsavedChanges.requestDiscard}
        loading={createRole.isPending}
        onDirtyChange={unsavedChanges.setIsDirty}
        onValuesChange={setDraft}
        onPermissionSummaryChange={setPermissionSummary}
      />

      <RoleUnsavedChangesModal
        open={unsavedChanges.confirmOpen}
        onKeepEditing={unsavedChanges.handleKeepEditing}
        onDiscard={unsavedChanges.handleDiscard}
      />
    </div>
  );
}
